import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials: { email: string; password: string }) =>
    api.post('/auth/login', credentials),
  
  register: (userData: any) =>
    api.post('/auth/register', userData),
  
  getProfile: () =>
    api.get('/auth/me'),
  
  updateProfile: (data: any) =>
    api.put('/auth/profile', data),
  
  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    api.put('/auth/change-password', data),
};

// Products API
export const productsAPI = {
  getProducts: (params?: any) =>
    api.get('/products', { params }),
  
  getProduct: (id: string) =>
    api.get(`/products/${id}`),
  
  getProductBySlug: (slug: string) =>
    api.get(`/products/slug/${slug}`),
  
  getFeaturedProducts: () =>
    api.get('/products/featured'),
  
  createProduct: (data: any) =>
    api.post('/products', data),
  
  updateProduct: (id: string, data: any) =>
    api.put(`/products/${id}`, data),
  
  deleteProduct: (id: string) =>
    api.delete(`/products/${id}`),
};

// Categories API
export const categoriesAPI = {
  getCategories: () =>
    api.get('/categories'),
  
  getCategoryTree: () =>
    api.get('/categories/tree'),
  
  getCategory: (id: string) =>
    api.get(`/categories/${id}`),
  
  getCategoryBySlug: (slug: string) =>
    api.get(`/categories/slug/${slug}`),
  
  createCategory: (data: any) =>
    api.post('/categories', data),
  
  updateCategory: (id: string, data: any) =>
    api.put(`/categories/${id}`, data),
  
  deleteCategory: (id: string) =>
    api.delete(`/categories/${id}`),
};

// Orders API
export const ordersAPI = {
  createOrder: (data: any) =>
    api.post('/orders', data),
  
  getOrders: (params?: any) =>
    api.get('/orders', { params }),
  
  getOrder: (id: string) =>
    api.get(`/orders/${id}`),
  
  updateOrderStatus: (id: string, data: { status: string; notes?: string }) =>
    api.put(`/orders/${id}/status`, data),
  
  getAllOrders: (params?: any) =>
    api.get('/orders/admin/all', { params }),
};

// Users API
export const usersAPI = {
  getUsers: (params?: any) =>
    api.get('/users', { params }),
  
  getUser: (id: string) =>
    api.get(`/users/${id}`),
  
  createUser: (data: any) =>
    api.post('/users', data),
  
  updateUser: (id: string, data: any) =>
    api.put(`/users/${id}`, data),
  
  deleteUser: (id: string) =>
    api.delete(`/users/${id}`),
  
  getUserStats: () =>
    api.get('/users/stats/overview'),
};

// Loyalty API
export const loyaltyAPI = {
  getProfile: () =>
    api.get('/loyalty/profile'),
  
  getRewards: () =>
    api.get('/loyalty/rewards'),
  
  redeemReward: (data: { rewardId: string; orderId?: string }) =>
    api.post('/loyalty/redeem', data),
  
  getTransactions: (params?: any) =>
    api.get('/loyalty/transactions', { params }),
  
  getLeaderboard: (params?: any) =>
    api.get('/loyalty/leaderboard', { params }),
  
  getConfig: () =>
    api.get('/loyalty/config'),
  
  createReward: (data: any) =>
    api.post('/loyalty/rewards', data),
};

export default api;
